import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';
import React, {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useRecoilValue} from 'recoil';
import {
  EAutocomplete,
  EForm,
  ESmallButton,
  ETextArea,
} from 'webapp-lib/Components';

import {ActionButtons, Loading} from '#';
import handledBackend, {
  TaskDueDateReachedNotification,
  TaskEntity,
  TaskResponse,
} from '@lexian/api/notifications-api';
import {Task} from '_/assets/images';
import {useRouteNavigate} from '_/hooks/useRouteNavigate';
import {I18NextNS} from '_/i18n';
import {featureFlagState} from '_/state/featureFlags';
import {State} from '_/utilities';
import {EFDateInput, useForm} from '_/utilities/form';
import {Loadable} from '_/utilities/types';

import {StoreContext} from '../../store';

import {ExclamationMark, FormHeader, HeaderRow, TextWrapper} from './styles';

export type Props = {
  notification: TaskDueDateReachedNotification;
};

enum TaskDueDateReachedFormFields {
  Date = 'date',
}

const isTaskResponse = (task: unknown): task is TaskResponse =>
  Boolean((task as TaskResponse).id);

const UTaskDueDateReached = ({notification}: Props): React.ReactElement => {
  const {entity} = notification;
  const [loadable, setLoadable] = useState<Loadable<TaskResponse>>({
    state: State.Loading,
  });

  useEffect(() => {
    if (!entity) {
      return;
    }
    handledBackend.getTask({
      parameters: {
        casusId: entity.casusId,
        taskId: entity.id,
      },
      onSuccess: ({body}) => {
        setLoadable(
          isTaskResponse(body)
            ? {
                state: State.Success,
                data: body,
              }
            : {state: State.Error}
        );
      },
    });
  }, [setLoadable, entity]);

  return (
    <div>
      <HeaderRow>
        <Task />
        <ExclamationMark show={notification.importantFlag} />
        <TextWrapper>{notification.message}</TextWrapper>
      </HeaderRow>
      {entity && loadable.state === State.Success ? (
        <TaskDueDateForm
          entity={entity}
          notification={notification}
          task={loadable.data!}
        />
      ) : entity ? (
        <Loading />
      ) : null}
    </div>
  );
};

const TaskDueDateForm = ({
  notification,
  task,
  entity,
}: {
  notification: TaskDueDateReachedNotification;
  task: TaskResponse;
  entity: TaskEntity;
}) => {
  const store = useContext(StoreContext);
  const {t} = useTranslation<I18NextNS>('notifications');
  const {globalTasks} = useRecoilValue(featureFlagState);
  const {goToCasusTasks} = useRouteNavigate();
  const {handleSubmit, control} = useForm({
    defaultValues: {
      [TaskDueDateReachedFormFields.Date]: task.dueDate,
    },
    onSubmit: (values) => {
      store.resolveTask({
        notificationId: notification.id,
        rescheduleDate: values[TaskDueDateReachedFormFields.Date],
      });
    },
  });

  const completeTask = () => {
    store.resolveTask({notificationId: notification.id});
  };

  return (
    <EForm onSubmit={handleSubmit}>
      <FormHeader>{t('taskDueDateReached.taskDetail')}</FormHeader>
      <ETextArea
        label={t('taskDueDateReached.description')}
        value={task.description}
        disabled
      />
      <EFDateInput
        control={control}
        label={t('taskDueDateReached.date')}
        name={TaskDueDateReachedFormFields.Date}
      />
      {!globalTasks ? (
        <EAutocomplete
          label={t('taskDueDateReached.assignee')}
          options={task.assignedUsers.map((it) => ({
            value: it.id,
            displayText: it.displayName,
          }))}
          value={task.assignedUsers[0]?.id}
          disabled
        />
      ) : null}
      <SplittedActionButtons>
        <ESmallButton type='submit'>
          {t('taskDueDateReached.reschedule')}
        </ESmallButton>
        <ESmallButton onClick={completeTask}>
          {t('taskDueDateReached.complete')}
        </ESmallButton>
        <ESmallButton
          onClick={() =>
            goToCasusTasks({
              casusId: entity.casusId,
              taskId: entity.id,
            })
          }
        >
          {t('common:actions.goToSource')}
        </ESmallButton>
      </SplittedActionButtons>
    </EForm>
  );
};

const SplittedActionButtons = styled(ActionButtons)`
  span:last-of-type {
    margin-left: auto;
  }
`;

export const TaskDueDateReached = observer(UTaskDueDateReached);
